import { useEffect, useId, useRef, useState } from 'react'
import { submitFeedback } from '../../shared/api/feedback'
import {
  FEEDBACK_COPY,
  charactersLeftText,
  describeFeedbackError,
  type Lang,
} from './feedbackCopy'
import {
  EMPTY_DRAFT,
  NPS_SCALE,
  RATING_SCALE,
  canSubmit,
  charactersLeft,
  toSubmission,
  validateDraft,
  type Draft,
} from './feedbackModel'

/**
 * H2 — the parent feedback modal.
 *
 * 🔴 EVERY DECISION THIS COMPONENT MAKES LIVES IN `feedbackModel.ts`. The repo
 * has no DOM renderer (OD-60) and this slice does not add one, so a rule written
 * inline here would be a rule with no test. What is left in this file is markup,
 * focus handling and one fetch.
 *
 * ⚠️ IT TAKES NO AUTH PROP AND SENDS NO TOKEN. `api.md` §2 lists feedback submit
 * as public, and the row is anonymous — so the modal works for a logged-out
 * parent, which is the audience most likely to answer candidly about a product
 * they have stopped using.
 */
export interface FeedbackModalProps {
  open: boolean
  onClose: () => void
  lang?: Lang
}

export function FeedbackModal({ open, onClose, lang = 'en' }: FeedbackModalProps) {
  const copy = FEEDBACK_COPY[lang]
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [state, setState] = useState<'editing' | 'sending' | 'sent'>('editing')
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const headingId = useId()
  const closeRef = useRef<HTMLButtonElement | null>(null)

  /*
   * Reset when it reopens. Without this, a parent who sent feedback, closed, and
   * opened it again would be looking at their previous answers with a "thank
   * you" still on screen.
   */
  useEffect(() => {
    if (!open) return
    setDraft(EMPTY_DRAFT)
    setState('editing')
    setError(null)
    setTouched(false)
    closeRef.current?.focus()
  }, [open])

  // Escape closes. A modal a keyboard user cannot dismiss is a trap, and this
  // one is entirely optional to fill in.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  const problems = validateDraft(draft)
  const left = charactersLeft(draft)

  const send = (): void => {
    setTouched(true)
    if (!canSubmit(draft)) return
    setState('sending')
    setError(null)
    submitFeedback(toSubmission(draft))
      .then(() => {
        setState('sent')
      })
      .catch((err: unknown) => {
        setState('editing')
        setError(describeFeedbackError(err, lang))
      })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        /*
         * ⚠️ `max-h-[90dvh] overflow-y-auto` — A MODAL WITH A TEXTAREA IN IT IS
         * TALLER THAN A PHONE HELD SIDEWAYS (390px), AND THE OVERLAY CENTRES IT.
         * Without a cap the send button is off the bottom of the screen with
         * nothing to scroll; `dvh` rather than `vh` so the collapsing mobile URL
         * bar does not leave the panel measured against a viewport that is gone.
         *
         * ⚠️ `p-5 sm:p-6` — 24px either side of a 360px screen inside the
         * overlay's own 16px is 80px of 360 spent before the text starts.
         */
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id={headingId} className="text-lg font-semibold text-slate-900">
            {copy.title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
          >
            {copy.close}
          </button>
        </div>

        {state === 'sent' ? (
          <p className="mt-6 text-slate-700">{copy.thanks}</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-slate-600">{copy.intro}</p>

            <fieldset className="mt-5">
              <legend className="text-sm font-medium text-slate-800">{copy.ratingLabel}</legend>
              <div className="mt-2 flex gap-2">
                {RATING_SCALE.map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={draft.rating === n}
                    onClick={() => {
                      setDraft((d) => ({ ...d, rating: n }))
                    }}
                    className={`h-11 w-11 rounded-lg border text-base ${
                      draft.rating === n
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 text-slate-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-xs text-slate-500">
                <span>{copy.ratingLow}</span>
                <span>{copy.ratingHigh}</span>
              </div>
            </fieldset>

            <fieldset className="mt-5">
              <legend className="text-sm font-medium text-slate-800">{copy.npsLabel}</legend>
              {/* Stated, because an unanswered NPS is a non-response and not a zero. */}
              <p className="text-xs text-slate-500">{copy.npsOptional}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {NPS_SCALE.map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={draft.nps === n}
                    onClick={() => {
                      setDraft((d) => ({ ...d, nps: d.nps === n ? null : n }))
                    }}
                    className={`h-9 w-9 rounded-md border text-sm ${
                      draft.nps === n
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 text-slate-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-xs text-slate-500">
                <span>{copy.npsLow}</span>
                <span>{copy.npsHigh}</span>
              </div>
            </fieldset>

            <label className="mt-5 block">
              <span className="text-sm font-medium text-slate-800">{copy.commentLabel}</span>
              <textarea
                value={draft.comment}
                placeholder={copy.commentPlaceholder}
                rows={3}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, comment: e.target.value }))
                }}
                className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              />
              <span className="text-xs text-slate-500">{charactersLeftText(left, lang)}</span>
            </label>

            {/*
              🔴 THE ANONYMITY PROMISE, NEXT TO THE BOX IT IS ABOUT. A parent
              decides how candid to be on the strength of this sentence, and the
              schema currently keeps it — see `feedbackCopy.ts`.
            */}
            <p className="mt-3 text-xs text-slate-500">{copy.anonymous}</p>

            {touched && problems.includes('NO_RATING') ? (
              <p className="mt-3 text-sm text-rose-700">{copy.needRating}</p>
            ) : null}
            {problems.includes('COMMENT_TOO_LONG') ? (
              <p className="mt-3 text-sm text-rose-700">{copy.tooLong}</p>
            ) : null}
            {error === null ? null : <p className="mt-3 text-sm text-rose-700">{error}</p>}

            <button
              type="button"
              onClick={send}
              disabled={state === 'sending'}
              className="mt-5 w-full rounded-lg bg-slate-900 px-4 py-3 text-white disabled:opacity-60"
            >
              {state === 'sending' ? copy.sending : copy.submit}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
